import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import { editRegistry, messageFor, staleRow, type RegistryRow } from "./api";
import { defaultMinDays, draftFor, minDaysValid, registryEditBody, saveMessage, storeLabel } from "./format";

export function RegistryEditor({
  row,
  areaName,
  onSaved,
}: {
  row: RegistryRow;
  areaName: string;
  onSaved: (row: RegistryRow) => void;
}) {
  const [draft, setDraft] = useState(draftFor(row));
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const body = registryEditBody(row, draft);
  const canSave = body != null && minDaysValid(draft.min_days) && !saving;
  const fallback = defaultMinDays(row);

  async function save() {
    if (!body) return;
    setSaving(true);
    setResult(null);
    try {
      const res = await editRegistry(body);
      setResult({ kind: "ok", text: saveMessage(res.data) });
      // The saved row comes back normalised ("45" → "45.00"); restart the draft from it.
      setDraft(draftFor(res.data.row));
      onSaved(res.data.row);
    } catch (error) {
      setResult({ kind: "error", text: messageFor(error, areaName) });
      // A stale save (C2 v1.4 D13) carries the row as it stands: show it, keep the draft, so the
      // next Save carries the current version and the operator's change is not lost.
      const fresh = staleRow(error);
      if (fresh) onSaved(fresh);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{row.sku} · {storeLabel(row)}</CardTitle>
        <p className="text-sm text-muted-foreground">{row.name}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {row.row_kind === "case" ? (
          <p className="text-sm text-ks-ink">Case rows carry no threshold.</p>
        ) : (
          <div className="flex flex-col gap-1">
            <Label htmlFor="ops-min-days">Minimum days of stock</Label>
            <Input
              id="ops-min-days"
              inputMode="numeric"
              value={draft.min_days}
              placeholder={fallback ? `${fallback} (leave blank to use the default)` : ""}
              onChange={(e) => setDraft({ ...draft, min_days: e.target.value })}
            />
            {!minDaysValid(draft.min_days) ? (
              <p role="alert" className="text-sm text-ks-danger">Enter a whole number of days above zero, or leave blank to use the default.</p>
            ) : null}
          </div>
        )}
        <div className="flex flex-col gap-1">
          <Label htmlFor="ops-status">Status</Label>
          <Select value={draft.status} onValueChange={(value) => setDraft({ ...draft, status: value as "active" | "inactive" })}>
            <SelectTrigger id="ops-status" className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">active</SelectItem>
              <SelectItem value="inactive">inactive (retired)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="ops-reason">Why are you changing this?</Label>
          <Textarea id="ops-reason" value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} />
        </div>
        <Button onClick={() => void save()} disabled={!canSave}>{saving ? "Saving…" : "Save"}</Button>
        {result ? (
          <p role={result.kind === "error" ? "alert" : "status"} className={`text-sm ${result.kind === "error" ? "text-ks-danger" : "text-ks-ink"}`}>
            {result.text}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
